import React, { useEffect, useState } from "react";
import "./RepTrainingPage.css";
import StandardModal from "../shared/Modal/StandardModal";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  Button,
  Group,
  Grid,
  Card,
  Stack,
  Text,
  Title,
  ThemeIcon,
  Table,
  TextInput,
  Select,
  Loader,
  Alert,
  Avatar,
  ActionIcon,
  Box,
  Badge,
  Progress,
  Divider,
} from "@mantine/core";
import {
  IconRefresh,
  IconX,
  IconCheck,
  IconUsers,
  IconVideo,
  IconClock,
  IconChartLine,
  IconSearch,
  IconAlertCircle,
} from "@tabler/icons-react";

function RepTrainingPage() {
  const { user, getVerifiedJWT } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("last_activity");
  const [filterByCourse, setFilterByCourse] = useState("all");
  const [activityDuring, setActivityDuring] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [showRepDetailModal, setShowRepDetailModal] = useState(false);
  const [allReps, setAllReps] = useState([]);
  const [courses, setCourses] = useState([]);
  const [summaryData, setSummaryData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shouldSearch, setShouldSearch] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Course history modal (chip click)
  const [courseHistoryModal, setCourseHistoryModal] = useState(null);

  useEffect(() => {
    fetchCourses();
    setShouldSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTrainingSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSearch]);

  const fetchCourses = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const courseScope =
        user?.role === "COMPANY_ADMIN" ? "company" : "manager";
      const response = await fetch(
        getApiEndpoint(`/v2/training/courses/${courseScope}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch courses");
      const details = await response.json();
      setCourses(details.data || []);
    } catch (err) {
      setCourses([]);
    }
  };

  const sortedReps = React.useMemo(() => {
    if (!allReps || allReps.length === 0) return [];
    const repsCopy = [...allReps];
    switch (sortBy) {
      case "name":
        return repsCopy.sort((a, b) => a.name.localeCompare(b.name));
      case "watch_time":
        return repsCopy.sort(
          (a, b) => (b.progress_watch_time || 0) - (a.progress_watch_time || 0)
        );
      case "watch_time_asc":
        return repsCopy.sort(
          (a, b) => (a.progress_watch_time || 0) - (b.progress_watch_time || 0)
        );
      case "videos_completed":
        return repsCopy.sort(
          (a, b) =>
            (b.progress_videos_completed || 0) -
            (a.progress_videos_completed || 0)
        );
      case "videos_completed_asc":
        return repsCopy.sort(
          (a, b) =>
            (a.progress_videos_completed || 0) -
            (b.progress_videos_completed || 0)
        );
      case "last_activity":
      default:
        return repsCopy.sort((a, b) => {
          const dateA = a.last_activity
            ? new Date(a.last_activity)
            : new Date(0);
          const dateB = b.last_activity
            ? new Date(b.last_activity)
            : new Date(0);
          return dateB - dateA;
        });
    }
  }, [allReps, sortBy]);

  const fetchTrainingSummary = async (isResync = false) => {
    if (shouldSearch || isResync) {
      try {
        if (isResync) {
          setSyncing(true);
        } else {
          setLoading(true);
          setAllReps([]);
        }

        const http_body = {
          search: searchTerm,
          sort_by: sortBy,
          start_date: startDate,
          end_date: endDate,
        };
        if (filterByCourse !== "all") {
          http_body.course_id = filterByCourse;
        }
        if (isResync) {
          http_body.resync = true;
        }

        const jwt = await getVerifiedJWT();
        const response = await fetch(
          getApiEndpoint("/v1/training/course-progress"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify(http_body),
          }
        );

        if (!response.ok)
          throw new Error("Failed to fetch training progress data");

        const details = await response.json();
        const data = details.data;
        setAllReps(data.reps || []);
        setSummaryData(data.totals || {});
        if (data.last_synced) setLastSynced(new Date(data.last_synced));
      } catch (err) {
        setError(err.message);
        setAllReps([]);
        setSummaryData({});
      } finally {
        setLoading(false);
        setSyncing(false);
        setShouldSearch(false);
      }
    }
  };

  const formatDuration = minutes => {
    if (!minutes) return "0m";
    const days = Math.floor(minutes / (60 * 24));
    const hours = Math.floor((minutes % (60 * 24)) / 60);
    const remainingMinutes = Math.ceil(minutes % 60);
    if (minutes >= 1440) {
      return hours > 0
        ? `${days}d ${hours}h ${remainingMinutes}m`
        : `${days}d ${remainingMinutes}m`;
    }
    if (minutes >= 60) return `${hours}h ${remainingMinutes}m`;
    return `${remainingMinutes}m`;
  };

  const formatAverageDuration = minutes => {
    if (!minutes) return "0m";
    const totalMinutes = Math.ceil(minutes);
    if (totalMinutes >= 1440) {
      const days = Math.floor(totalMinutes / 1440);
      const remainingMinutes = totalMinutes % 1440;
      const hours = Math.floor(remainingMinutes / 60);
      const mins = remainingMinutes % 60;
      if (hours > 0 && mins > 0) return `${days}d ${hours}h ${mins}m`;
      if (hours > 0) return `${days}d ${hours}h`;
      if (mins > 0) return `${days}d ${mins}m`;
      return `${days}d`;
    }
    if (totalMinutes >= 60) {
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${totalMinutes}m`;
  };

  const formatDate = dateString => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    }
    return date.toLocaleDateString();
  };

  const formatLastSynced = () => {
    if (!lastSynced) return "Never";
    const now = new Date();
    const diff = now - lastSynced;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (seconds < 60) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return lastSynced.toLocaleDateString();
  };

  useEffect(() => {
    if (activityDuring === "all") {
      setStartDate(null);
      setEndDate(null);
      setShouldSearch(true);
    } else {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let start_date = null;
      const end_date = now.toISOString();
      switch (activityDuring) {
        case "today":
          start_date = today.toISOString();
          break;
        case "last7days":
          start_date = new Date(
            today.getTime() - 7 * 24 * 60 * 60 * 1000
          ).toISOString();
          break;
        case "last30days":
          start_date = new Date(
            today.getTime() - 30 * 24 * 60 * 60 * 1000
          ).toISOString();
          break;
        case "last90days":
          start_date = new Date(
            today.getTime() - 90 * 24 * 60 * 60 * 1000
          ).toISOString();
          break;
        default:
          break;
      }
      setStartDate(start_date);
      setEndDate(end_date);
      setShouldSearch(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityDuring]);

  useEffect(() => {
    setShouldSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterByCourse]);

  const handleSearchChange = e => {
    setSearchTerm(e.currentTarget.value);
  };

  const handleSearchKeyPress = e => {
    if (e.key === "Enter") setShouldSearch(true);
  };

  const handleResync = () => {
    setSearchTerm("");
    setSortBy("last_activity");
    setFilterByCourse("all");
    setActivityDuring("all");
    setStartDate(null);
    setEndDate(null);
    fetchTrainingSummary(true);
  };

  // ─── Course chip click → opens video history modal ───────────────────────
  const handleCourseChipClick = async (e, rep, course) => {
    e.stopPropagation(); // don't trigger row click

    // Immediately open modal with existing watch-log data filtered to this course
    const watchedLogs = (rep.all_video_logs || []).filter(
      log => String(log.course_id) === String(course.id)
    );

    // Find accurate course-level stats from rep.courses (TrainingVideoLog source)
    const repCourse =
      (rep.courses || []).find(c => c.name === course.name) || null;

    setCourseHistoryModal({
      open: true,
      rep,
      course,
      repCourse,
      loading: true,
      courseVideos: [],
      watchedLogs,
      videoCompletionLogs: [],
    });

    try {
      const jwt = await getVerifiedJWT();
      // Fetch course video list AND per-video completion in parallel
      const [courseRes, completionRes] = await Promise.all([
        fetch(getApiEndpoint(`/v1/training/course/${course.id}`), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }),
        fetch(getApiEndpoint("/v1/training/rep-video-progress"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ course_id: course.id, rep_id: rep.rep_id }),
        }),
      ]);

      const videos = courseRes.ok
        ? (await courseRes.json()).data?.videos || []
        : [];
      const videoCompletionLogs = completionRes.ok
        ? (await completionRes.json()).data?.videos || []
        : [];

      setCourseHistoryModal(prev =>
        prev
          ? {
              ...prev,
              loading: false,
              courseVideos: videos,
              videoCompletionLogs,
            }
          : prev
      );
    } catch {
      setCourseHistoryModal(prev =>
        prev ? { ...prev, loading: false } : prev
      );
    }
  };

  // Helper: format seconds into mm:ss
  const formatSeconds = secs => {
    if (!secs) return "0:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <Stack gap="lg">
      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="blue">
                <IconUsers size={22} />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  Active Reps
                </Text>
                <Text fz="2xl" fw={700}>
                  {loading ? "—" : (summaryData.active_reps ?? 0)}
                </Text>
              </Box>
              <Text size="sm" c="dimmed">
                With training activity
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="violet">
                <IconVideo size={22} />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  Videos Completed
                </Text>
                <Text fz="2xl" fw={700}>
                  {loading ? "—" : (summaryData.total_videos_completed ?? 0)}
                </Text>
              </Box>
              <Text size="sm" c="dimmed">
                Across all reps
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="teal">
                <IconClock size={22} />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  Total Watch Time
                </Text>
                <Text fz="2xl" fw={700}>
                  {loading ? "—" : formatDuration(summaryData.total_watch_time)}
                </Text>
              </Box>
              <Text size="sm" c="dimmed">
                Combined across all reps
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="orange">
                <IconChartLine size={22} />
              </ThemeIcon>
              <Box>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  Avg Watch Time
                </Text>
                <Text fz="2xl" fw={700}>
                  {loading
                    ? "—"
                    : formatAverageDuration(summaryData.average_watch_time)}
                </Text>
              </Box>
              <Text size="sm" c="dimmed">
                Per rep average
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* ── Rep Progress Table ────────────────────────────────────────────── */}
      <Card padding={0} radius="md" withBorder>
        <Stack gap="lg">
          {/* Card header — mirrors the Overview "Sales Representatives" header */}
          <Group justify="space-between" align="center" p="lg" pb="md">
            <Title order={3}>Rep Training Progress</Title>

            <Group gap="sm">
              {/* Search */}
              <TextInput
                placeholder="Search reps by name..."
                leftSection={<IconSearch size={16} />}
                rightSection={
                  searchTerm ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      onClick={() => {
                        setSearchTerm("");
                        setShouldSearch(true);
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyPress}
                style={{ minWidth: 250 }}
              />

              {/* Activity period */}
              <Select
                value={activityDuring}
                onChange={val => setActivityDuring(val || "all")}
                data={[
                  { value: "all", label: "Filter by Time" },
                  { value: "today", label: "Today" },
                  { value: "last7days", label: "Last 7 Days" },
                  { value: "last30days", label: "Last 30 Days" },
                  { value: "last90days", label: "Last 90 Days" },
                ]}
                style={{ minWidth: 150 }}
                allowDeselect={false}
              />

              {/* Filter by course */}
              <Select
                value={filterByCourse}
                onChange={val => setFilterByCourse(val || "all")}
                data={[
                  { value: "all", label: "Filter by Course" },
                  ...courses.map(c => ({
                    value: String(c.id),
                    label: c.course_title,
                  })),
                ]}
                style={{ minWidth: 160 }}
                allowDeselect={false}
              />

              {/* Sort */}
              <Select
                value={sortBy}
                onChange={val => setSortBy(val || "last_activity")}
                data={[
                  { value: "last_activity", label: "Filter by Metric" },
                  { value: "name", label: "Name" },
                  { value: "watch_time", label: "Most Watch Time" },
                  { value: "watch_time_asc", label: "Least Watch Time" },
                  { value: "videos_completed", label: "Most Videos" },
                  { value: "videos_completed_asc", label: "Least Videos" },
                ]}
                style={{ minWidth: 160 }}
                allowDeselect={false}
              />

              {/* Sync */}
              <div style={{ position: "relative" }}>
                <Button
                  variant="outline"
                  leftSection={<IconRefresh size={16} />}
                  onClick={handleResync}
                  loading={syncing}
                  disabled={syncing}
                >
                  {syncing ? "Syncing..." : "Sync"}
                </Button>
                {lastSynced && !loading && (
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      whiteSpace: "nowrap",
                      paddingTop: 2,
                    }}
                  >
                    Synced {formatLastSynced()}
                  </Text>
                )}
              </div>
            </Group>
          </Group>

          {/* Loading */}
          {loading && (
            <Group justify="center" py="xl">
              <Loader size="md" />
              <Text c="dimmed">Loading training data...</Text>
            </Group>
          )}

          {/* Error */}
          {error && !loading && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Error"
              color="red"
            >
              <Group justify="space-between">
                <Text>{error}</Text>
                <Button
                  variant="outline"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={() => {
                    setError(null);
                    setShouldSearch(true);
                  }}
                >
                  Try Again
                </Button>
              </Group>
            </Alert>
          )}

          {/* Table */}
          {!loading && !error && (
            <Box style={{ overflow: "hidden" }}>
              {sortedReps.length === 0 ? (
                <Text ta="center" c="dimmed" py="xl">
                  {searchTerm
                    ? `No reps found matching "${searchTerm}"`
                    : activityDuring !== "all"
                      ? "No training activity in the selected time period"
                      : "No reps found"}
                </Text>
              ) : (
                <div
                  className="rep-training-table-wrapper"
                  style={{ overflow: "hidden" }}
                >
                  <Table
                    highlightOnHover
                    verticalSpacing="md"
                    horizontalSpacing="lg"
                    stickyHeader
                  >
                    <Table.Thead>
                      <Table.Tr>
                        {[
                          "Name",
                          "Last Activity",
                          "Watch Time",
                          "Videos Completed",
                          "Courses Watched",
                        ].map(header => (
                          <Table.Th
                            key={header}
                            style={{
                              background: "#fafbfc",
                              borderTop: "1px solid #e5e7eb",
                            }}
                          >
                            {header}
                          </Table.Th>
                        ))}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {sortedReps.map(rep => (
                        <Table.Tr
                          key={rep.rep_id}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedRep(rep);
                            setShowRepDetailModal(true);
                          }}
                        >
                          {/* Name */}
                          <Table.Td>
                            <Group gap="md" wrap="nowrap">
                              <Avatar
                                src={rep.profile_image_url || undefined}
                                radius="xl"
                                size="lg"
                                color="blue"
                              >
                                {rep.first_name?.charAt(0)}
                                {rep.last_name?.charAt(0)}
                              </Avatar>
                              <Text fw={500}>{rep.name}</Text>
                            </Group>
                          </Table.Td>

                          {/* Last Activity */}
                          <Table.Td>
                            <Text c={rep.last_activity ? "inherit" : "dimmed"}>
                              {formatDate(rep.last_activity)}
                            </Text>
                          </Table.Td>

                          {/* Watch Time */}
                          <Table.Td>
                            <Text fw={500}>
                              {formatDuration(rep.progress_watch_time)}
                            </Text>
                          </Table.Td>

                          {/* Videos Completed */}
                          <Table.Td>
                            <Text fw={500}>
                              {rep.progress_videos_completed}
                            </Text>
                          </Table.Td>

                          {/* Courses Watched */}
                          <Table.Td className="rep-training-courses-cell">
                            {rep.progress_courses_watched &&
                            rep.progress_courses_watched.length > 0 ? (
                              <Group gap={6} wrap="wrap">
                                {rep.progress_courses_watched.map(course => (
                                  <div
                                    key={`course-${course.id}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={e =>
                                      handleCourseChipClick(e, rep, course)
                                    }
                                    onKeyDown={e => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        handleCourseChipClick(e, rep, course);
                                      }
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 8,
                                      padding: "7px 14px 7px 7px",
                                      backgroundColor: "#ffffff",
                                      borderRadius: 999,
                                      border: "1px solid #e5e7eb",
                                      cursor: "pointer",
                                      transition:
                                        "border-color 0.15s, box-shadow 0.15s",
                                    }}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.borderColor =
                                        "#9ca3af";
                                      e.currentTarget.style.boxShadow =
                                        "0 1px 4px rgba(0,0,0,0.08)";
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.borderColor =
                                        "#e5e7eb";
                                      e.currentTarget.style.boxShadow = "none";
                                    }}
                                  >
                                    {course.image ? (
                                      <img
                                        src={course.image}
                                        alt={course.name}
                                        style={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: 6,
                                          objectFit: "cover",
                                          flexShrink: 0,
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: 6,
                                          backgroundColor: "#e5e7eb",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          flexShrink: 0,
                                        }}
                                      >
                                        <IconVideo size={12} color="#9ca3af" />
                                      </div>
                                    )}
                                    <Text size="md" fw={500} c="#111827">
                                      {course.name}
                                    </Text>
                                  </div>
                                ))}
                              </Group>
                            ) : (
                              <Text c="dimmed">—</Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              )}
            </Box>
          )}
        </Stack>
      </Card>

      {/* ── Rep Detail Modal ──────────────────────────────────────────────── */}
      <StandardModal
        isOpen={showRepDetailModal && selectedRep}
        onClose={() => setShowRepDetailModal(false)}
        title={
          <div style={{ marginBottom: "1.5rem" }}>
            <Group gap="md" align="center">
              <Avatar
                src={selectedRep?.profile_image_url || undefined}
                radius="xl"
                size="lg"
                color="blue"
              >
                {selectedRep?.first_name?.charAt(0)}
                {selectedRep?.last_name?.charAt(0)}
              </Avatar>
              <div>
                <Text fw={700} size="lg">
                  {selectedRep?.first_name} {selectedRep?.last_name}
                </Text>
                <Text size="sm" c="dimmed">
                  Training Progress Details
                </Text>
              </div>
            </Group>
          </div>
        }
      >
        {selectedRep && (
          <div style={{ padding: "1.5rem 2rem" }}>
            {/* Overview Stats */}
            <Text fw={600} size="sm" mb="sm">
              User Summary
            </Text>
            <Grid mb="lg">
              <Grid.Col span={6}>
                <Card padding="md" radius="md" withBorder>
                  <Text size="xs" c="dimmed" mb={4}>
                    Total Watch Time
                  </Text>
                  <Text fw={700} size="lg">
                    {formatDuration(selectedRep.total_watch_time)}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={6}>
                <Card padding="md" radius="md" withBorder>
                  <Text size="xs" c="dimmed" mb={4}>
                    Videos Completed
                  </Text>
                  <Text fw={700} size="lg">
                    {selectedRep.videos_completed}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={6}>
                <Card padding="md" radius="md" withBorder>
                  <Text size="xs" c="dimmed" mb={4}>
                    Total Courses
                  </Text>
                  <Text fw={700} size="lg">
                    {selectedRep.total_courses || 0}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={6}>
                <Card padding="md" radius="md" withBorder>
                  <Text size="xs" c="dimmed" mb={4}>
                    Last Activity
                  </Text>
                  <Text fw={700} size="lg">
                    {formatDate(selectedRep.last_activity)}
                  </Text>
                </Card>
              </Grid.Col>
            </Grid>

            {/* Course Progress */}
            <Text fw={600} size="sm" mb="sm">
              Course Progress
            </Text>
            {selectedRep.courses && selectedRep.courses.length > 0 ? (
              <Stack gap="sm">
                {selectedRep.courses.map((course, idx) => {
                  const pct = course.completed_percentage;
                  const isCompleted = pct >= 100;
                  return (
                    <Card
                      key={idx}
                      padding="md"
                      radius="md"
                      withBorder
                      style={{ cursor: "pointer" }}
                      onClick={e => {
                        // selectedRep.courses lacks id/image — look up the enriched
                        // chip object from progress_courses_watched (which has id)
                        const enriched =
                          (selectedRep.progress_courses_watched || []).find(
                            c => c.name === course.name
                          ) || course;
                        handleCourseChipClick(e, selectedRep, enriched);
                      }}
                    >
                      <Group justify="space-between" mb={6}>
                        <Group gap="xs">
                          {isCompleted && (
                            <IconCheck
                              size={16}
                              color="var(--mantine-color-green-6)"
                            />
                          )}
                          <Text fw={500} size="sm">
                            {course.name}
                          </Text>
                        </Group>
                        <Text
                          size="sm"
                          fw={600}
                          c={isCompleted ? "green" : "dimmed"}
                        >
                          {isCompleted
                            ? "Completed"
                            : pct > 0
                              ? `${Math.round(pct)}%`
                              : "Not started"}
                        </Text>
                      </Group>
                      {/* Progress bar */}
                      <div
                        style={{
                          height: 6,
                          borderRadius: 3,
                          background: "#e9ecef",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${Math.min(pct, 100)}%`,
                            background: isCompleted
                              ? "var(--mantine-color-green-6)"
                              : "var(--mantine-color-orange-6)",
                            borderRadius: 3,
                          }}
                        />
                      </div>
                      <Group gap="lg" mt={6}>
                        <Text size="xs" c="dimmed">
                          {course.completed_video_count}/{course.video_count}{" "}
                          videos
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatDuration(course.watch_time)} watch time
                        </Text>
                        {course.last_watched && (
                          <Text size="xs" c="dimmed">
                            Last: {formatDate(course.last_watched)}
                          </Text>
                        )}
                      </Group>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Text c="dimmed" size="sm">
                No course activity yet
              </Text>
            )}
          </div>
        )}
      </StandardModal>

      {/* ── Course Video History Modal (chip click) ───────────────────────── */}
      {courseHistoryModal && (
        <StandardModal
          isOpen={courseHistoryModal.open}
          onClose={() => setCourseHistoryModal(null)}
          title={
            <div style={{ marginBottom: "1rem" }}>
              <Group gap="md" align="center">
                {courseHistoryModal.course.image ? (
                  <img
                    src={courseHistoryModal.course.image}
                    alt={courseHistoryModal.course.name}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <ThemeIcon size={44} radius="md" variant="light" color="blue">
                    <IconVideo size={22} />
                  </ThemeIcon>
                )}
                <div>
                  <Text fw={700} size="lg">
                    {courseHistoryModal.course.name}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {courseHistoryModal.rep.name} — Video Watch History
                  </Text>
                </div>
              </Group>
            </div>
          }
        >
          <div style={{ padding: "0 2rem 1.5rem" }}>
            {courseHistoryModal.loading ? (
              <Group justify="center" py="xl">
                <Loader size="sm" />
                <Text c="dimmed" size="sm">
                  Loading video details...
                </Text>
              </Group>
            ) : courseHistoryModal.courseVideos.length > 0 ? (
              <Stack gap="sm">
                <Divider mb="sm" />
                {/* Progress summary — use repCourse (TrainingVideoLog) for accuracy */}
                {(() => {
                  const rc = courseHistoryModal.repCourse;
                  const total = rc
                    ? rc.video_count
                    : courseHistoryModal.courseVideos.length;
                  const completed = rc ? rc.completed_video_count : 0;
                  const pct =
                    total > 0 ? Math.round((completed / total) * 100) : 0;
                  return (
                    <Card padding="sm" radius="md" withBorder mb="xs">
                      <Group justify="space-between" mb={6}>
                        <Text size="sm" fw={600}>
                          Overall Progress
                        </Text>
                        <Text
                          size="sm"
                          fw={700}
                          c={pct === 100 ? "green" : "orange"}
                        >
                          {completed}/{total} videos · {pct}%
                        </Text>
                      </Group>
                      <Progress
                        value={pct}
                        color={pct === 100 ? "green" : "orange"}
                        size="sm"
                        radius="xl"
                      />
                    </Card>
                  );
                })()}

                {/* Video list */}
                {courseHistoryModal.courseVideos.map((video, idx) => {
                  // Use accurate TrainingVideoLog data from the new endpoint
                  const compLog = courseHistoryModal.videoCompletionLogs.find(
                    l => String(l.video_id) === String(video.id)
                  );
                  const isCompleted = !!compLog?.video_completed;
                  const watchedSecs = compLog?.seconds_watched || 0;
                  const hasStarted = watchedSecs > 0 || isCompleted;
                  const thumbUrl =
                    video.video_thumbnail ||
                    courseHistoryModal.course.image ||
                    null;

                  return (
                    <Card key={video.id} padding="md" radius="md" withBorder>
                      <Group gap="md" wrap="nowrap" align="center">
                        {/* Thumbnail */}
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt={video.video_title}
                              style={{
                                width: 96,
                                height: 60,
                                borderRadius: 8,
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 96,
                                height: 60,
                                borderRadius: 8,
                                backgroundColor: "#e9ecef",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <IconVideo size={20} color="#9ca3af" />
                            </div>
                          )}
                          {isCompleted && (
                            <div
                              style={{
                                position: "absolute",
                                top: -4,
                                right: -4,
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: "var(--mantine-color-green-5)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <IconCheck size={10} color="white" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Group gap={6} mb={2}>
                            <Text size="xs" c="dimmed" fw={500}>
                              #{idx + 1}
                            </Text>
                            <Text size="md" fw={600} lineClamp={1}>
                              {video.video_title}
                            </Text>
                          </Group>
                          <Group gap="xs">
                            {/* Only show badge if we have real data for this video */}
                            {(isCompleted || hasStarted) && (
                              <Badge
                                size="md"
                                radius="sm"
                                color={isCompleted ? "green" : "blue"}
                                variant="light"
                              >
                                {isCompleted
                                  ? "✓ Completed"
                                  : `In progress · ${formatSeconds(watchedSecs)} watched`}
                              </Badge>
                            )}
                            {video.duration && (
                              <Text size="sm" c="dimmed">
                                {formatSeconds(video.duration)} total
                              </Text>
                            )}
                          </Group>
                        </Box>
                      </Group>
                    </Card>
                  );
                })}
              </Stack>
            ) : courseHistoryModal.watchedLogs.length > 0 ? (
              /* Fallback: show raw watch log data when video titles unavailable */
              <Stack gap="sm">
                <Text size="xs" c="dimmed" mb="xs">
                  {courseHistoryModal.watchedLogs.length} video
                  {courseHistoryModal.watchedLogs.length !== 1 ? "s" : ""}{" "}
                  watched
                </Text>
                {courseHistoryModal.watchedLogs.map((log, idx) => (
                  <Card key={idx} padding="sm" radius="md" withBorder>
                    <Group justify="space-between">
                      <Text size="sm" fw={500}>
                        Video #{idx + 1}
                      </Text>
                      <Group gap="xs">
                        <Badge
                          size="xs"
                          color={log.video_completed ? "green" : "blue"}
                          variant="light"
                        >
                          {log.video_completed
                            ? "Completed"
                            : `${formatSeconds(log.watched_seconds)} watched`}
                        </Badge>
                      </Group>
                    </Group>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Text c="dimmed" size="sm" ta="center" py="xl">
                No watch history found for this course.
              </Text>
            )}
          </div>
        </StandardModal>
      )}
    </Stack>
  );
}

export default RepTrainingPage;
